import { HttpErrorResponse } from '@angular/common/http';
import { SwaggerException } from '../sdk/sdk';

// MSAL BrowserAuthError codes for a failed network call. App code imports auth symbols only from
// ngx-groundworks-auth, which does not export MSAL's error classes, so MSAL errors are matched by
// name and errorCode. 'failed_to_parse_response' is what a gateway's 502 or 504 HTML page becomes.
// MSAL 5 raises 'timed_out' only when a silent-renewal iframe (or a redirect) never answers, not
// for a slow network, so it is not here: see isInteractionRequiredError.
const TRANSIENT_MSAL_ERROR_CODES = new Set([
  'no_network_connectivity',
  'post_request_failed',
  'get_request_failed',
  'failed_to_parse_response',
]);

// OAuth error codes for an unavailable token endpoint. MSAL builds ServerError from the endpoint's
// JSON body, which carries no HTTP status, so the code is all there is to go on.
const TRANSIENT_SERVER_ERROR_CODES = new Set(['temporarily_unavailable', 'server_error']);

export interface RetryOptions {
  /** The wait before each retry; the operation runs at most `delaysMs.length + 1` times. */
  delaysMs?: readonly number[];
}

/**
 * Runs `operation`, retrying it after each delay while it fails with a transient error.
 * A permanent error, or the last attempt's error, rejects the returned promise.
 */
export async function retryTransient<T>(
  operation: () => Promise<T>,
  { delaysMs = [1000, 2000] }: RetryOptions = {},
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await operation();
    } catch (error) {
      if (attempt >= delaysMs.length || !isTransientError(error)) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, delaysMs[attempt]));
    }
  }
}

/**
 * True for failures that can succeed on a later attempt: a network failure, or 429 or 5xx from
 * HttpClient or the generated API client, or an unavailable token endpoint. An
 * interaction-required MSAL error is permanent: only the user can resolve it.
 *
 * The generated client throws SwaggerException only for a status its endpoint does not declare
 * (status 0 included). For a declared error status it throws the typed body instead, such as
 * ProblemDetails on a declared 500, which this treats as permanent. listBranches declares none.
 */
export function isTransientError(error: unknown): boolean {
  if (error instanceof HttpErrorResponse || error instanceof SwaggerException) {
    return isTransientStatus(error.status);
  }
  if (!(error instanceof Error) || !('errorCode' in error)) {
    return false;
  }
  const errorCode = String(error.errorCode);
  if (error.name === 'ServerError') {
    return TRANSIENT_SERVER_ERROR_CODES.has(errorCode);
  }
  return error.name === 'BrowserAuthError' && TRANSIENT_MSAL_ERROR_CODES.has(errorCode);
}

/**
 * True for an MSAL InteractionRequiredAuthError (interaction_required, login_required,
 * consent_required, no_tokens_found and the like): the session has expired or needs consent, and
 * only an interactive sign-in can resolve it. Also true for a BrowserAuthError 'timed_out': the
 * silent-renewal iframe never answered, and retrying it only waits another 10 s for the same.
 */
export function isInteractionRequiredError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return (
    error.name === 'InteractionRequiredAuthError' ||
    (error.name === 'BrowserAuthError' && (error as { errorCode?: unknown }).errorCode === 'timed_out')
  );
}

function isTransientStatus(status: number): boolean {
  return status === 0 || status === 429 || status >= 500;
}
